<template>
  <div class="absolute inset-x-3 inset-y-[6px] pointer-events-none">
    <div
      v-for="m in matches"
      :key="m.id"
      class="absolute top-0 bottom-0 rounded-[3px] border-l-2 pointer-events-auto cursor-pointer transition-[opacity,filter] duration-150"
      :style="styleFor(m)"
      @pointerenter="onEnter(m, $event)"
      @pointerleave="onLeave"
    />
  </div>
</template>

<script setup lang="ts">
import { inject } from 'vue'
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { TimelineHoverKey, type TimelineSideMatch } from './timeline-types'

const props = defineProps<{
  matches: readonly TimelineSideMatch[]
  duration: number
}>()

const hover = inject(TimelineHoverKey, null)

const PALETTE: Record<ScoreLevel, { bg: string; border: string }> = {
  [ScoreLevel.High]: { bg: 'rgba(220, 60, 63, 0.15)', border: '#dc3c3f' },
  [ScoreLevel.Medium]: { bg: 'rgba(245, 185, 88, 0.15)', border: '#f5b958' },
  [ScoreLevel.Low]: { bg: 'rgba(52, 177, 199, 0.15)', border: '#34b1c7' },
}

const styleFor = (m: TimelineSideMatch) => {
  const dur = props.duration || 1
  const start = Math.max(0, (m.start / dur) * 100)
  const end = Math.min(100, (m.end / dur) * 100)
  const width = Math.max(0, end - start)
  const colors = PALETTE[m.level] ?? PALETTE[ScoreLevel.High]
  const id = hover?.hoveredId.value
  const dimmed = id && id !== m.id

  return {
    left: `${start}%`,
    width: `${width}%`,
    backgroundColor: colors.bg,
    borderColor: colors.border,
    opacity: dimmed ? 0.25 : 1,
    filter: dimmed ? 'grayscale(1)' : 'none',
  }
}

const onEnter = (m: TimelineSideMatch, e: PointerEvent) => {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  hover?.enter(m.match, {
    x: rect.left + rect.width / 2,
    top: rect.top,
    bottom: rect.bottom,
  })
}

const onLeave = () => {
  hover?.hide()
}
</script>
